import { TabSwitcher } from '@aus-platform/design-system';
import { has, isNil, omit } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { heapTabs } from '.';
import { useAppSelector } from '../../../../../app/hooks';
import type {
  ClusterHeap,
  HeapClusterResponseDataTypeObj,
} from '../../../../../shared/api';
import { ClusterStatus, useHeapList } from '../../../../../shared/api';
import {
  selectMap3DState,
  selectMap3dDataset,
} from '../../../../shared/map-3d-slices';
import {
  PendingResourceManager,
  ResourceDeepLinkParam,
} from '../../../../shared/resource-deep-links';
import { Map3DNoTerrainSelected } from '../../shared';
import type { SelectedClusterType, SelectedInactiveHeaps } from './components';
import { HeapClusterManagementTabs, HeapClusterTable } from './components';
import { flyToCentroid } from './components/helpers';
import type { ActiveHeapClusterType } from './types';

export const Map3DHeapManagement = () => {
  // States.
  const [selectedClusters, setSelectedClusters] =
    useState<SelectedClusterType | null>(null);
  const [isHeapsTableVisible, setIsHeapsTableVisible] =
    useState<boolean>(false);
  const [activeTabKey, setActiveTabKey] = useState(heapTabs.Draw);
  const [activeHeapCluster, setActiveHeapCluster] =
    useState<ActiveHeapClusterType | null>(null);
  const [isHeapClusterModified, setIsHeapClusterModified] =
    useState<boolean>(false);
  const [scrollToClusterId, setScrollToClusterId] = useState<string | null>(
    null,
  );

  // Selectors.
  const { selectedTerrainIteration } = useAppSelector(selectMap3dDataset);
  const { cesiumProxy, aoiManager } = useAppSelector(selectMap3DState);

  // Drawing tools.
  const polygonTool = aoiManager?.polygonTool;

  // Api.
  const {
    data: heapListResponse,
    isLoading: isHeapListLoading,
    refetch: heapListRefetch,
    isFetching: isHeapListFetching,
    isSuccess: isHeapListSuccess,
  } = useHeapList(
    { id: selectedTerrainIteration?.id ?? '' },
    !!selectedTerrainIteration?.id,
  );

  // useMemo.
  // Filtered out inactive heaps from the selected heap clusters.
  const selectedInactiveHeaps: SelectedInactiveHeaps = useMemo(() => {
    if (!heapListResponse?.data) {
      return [];
    }

    const filteredClusters = heapListResponse.data.filter(
      ({ id }) =>
        has(selectedClusters, id) && id !== activeHeapCluster?.clusterId,
    );

    return filteredClusters.flatMap(({ heaps }) =>
      heaps.map((heap: ClusterHeap) => ({
        id: heap.id,
        geometry: heap.geometry,
        heapName: heap.name,
      })),
    );
  }, [selectedClusters, activeHeapCluster, heapListResponse]);

  const heapClustersOptions = useMemo(() => {
    return heapListResponse?.data
      .filter(
        (heapCluster) =>
          heapCluster.numberOfHeaps !== 0 && selectedClusters?.[heapCluster.id],
      )
      .map(({ id, name }) => ({
        label: name,
        value: id,
      }));
  }, [heapListResponse, selectedClusters]);

  // useEffects.
  useEffect(() => {
    // To fetch current Heap list table after refetching Heap cluster list.
    if (isHeapListFetching && isHeapListSuccess) {
      heapListResponse?.data.forEach((heapCluster) => {
        if (
          heapCluster.id === activeHeapCluster?.clusterId &&
          heapCluster.status !== ClusterStatus.Done
        ) {
          setIsHeapClusterModified(true);
        }
      });
    }
  }, [
    isHeapListFetching,
    isHeapListSuccess,
    heapListResponse,
    activeHeapCluster,
  ]);

  useEffect(() => {
    // Adding polygon tool as dependency to ensure using the updated value when unmounting.
    return () => {
      // @TODO - use deleteAllPolygons function when the importWkt function is updated to return polygons with different IDs for MULTIPOLYGON WKT.
      polygonTool?.deletePolygonsByProperty('isHeap', true);
    };
  }, [polygonTool]);

  // Handlers.
  const importWKTPolygon = (
    label: string,
    heapId: string,
    geometry: string,
  ) => {
    polygonTool?.importWKT(
      geometry,
      heapId,
      {
        heapId,
        isHeap: true,
      },
      label,
      {
        polygonStyleOptions: {
          fillColor: '#F9CC3366',
          fillOpacity: 0.4,
        },
      },
    );
  };

  const onSelectClusterHandler = (
    selectedCluster: HeapClusterResponseDataTypeObj,
  ) => {
    if (selectedCluster.status === ClusterStatus.Processing) {
      return;
    }

    // Checking if the heap is already selected or not.
    if (!has(selectedClusters, selectedCluster.id)) {
      setSelectedClusters((previousSelectedHeap) => {
        return {
          ...previousSelectedHeap,
          [selectedCluster.id]: {
            ...selectedCluster,
          },
        };
      });

      if (!selectedCluster.numberOfHeaps) {
        importWKTPolygon(
          selectedCluster.name,
          selectedCluster.id,
          selectedCluster.geometry,
        );
      } else {
        selectedCluster.heaps.forEach((heap) => {
          importWKTPolygon(heap.name, heap.id, heap.geometry);
        });
      }
    } else {
      if (activeHeapCluster?.clusterId === selectedCluster.id) {
        setIsHeapsTableVisible(false);
        setActiveHeapCluster(null);
      }

      if (!selectedCluster.numberOfHeaps) {
        polygonTool?.deletePolygonsByProperty('heapId', selectedCluster.id);
      } else {
        selectedCluster.heaps.forEach((heap) => {
          polygonTool?.deletePolygonsByProperty('heapId', heap.id);
        });
      }

      setSelectedClusters((previousSelectedHeaps) => {
        // Remove heap from the list of selected heaps.
        return omit(previousSelectedHeaps, selectedCluster.id);
      });
    }
  };

  const onTabSwitch = (tabKey: string) => {
    setActiveTabKey(tabKey);

    if (tabKey !== heapTabs.List && tabKey !== heapTabs.Guide) {
      polygonTool?.deletePolygonsByProperty('isHeap', true);
      setSelectedClusters(null);
      setIsHeapsTableVisible(false);
    }
  };

  // Auto-select and activate heap cluster from sessionStorage pending id.
  useEffect(() => {
    const pendingClusterId = PendingResourceManager.get(
      ResourceDeepLinkParam.HeapCluster,
    );

    if (isNil(pendingClusterId)) {
      return;
    }

    if (
      !isHeapListSuccess ||
      isHeapListLoading ||
      isNil(heapListResponse?.data)
    ) {
      return;
    }

    const targetCluster = heapListResponse.data.find(
      (cluster) => cluster.id === pendingClusterId,
    );

    if (isNil(targetCluster)) {
      PendingResourceManager.remove(ResourceDeepLinkParam.HeapCluster);
      return;
    }

    setActiveTabKey(heapTabs.List);
    onSelectClusterHandler(targetCluster);

    if (targetCluster.centroid && cesiumProxy) {
      flyToCentroid(cesiumProxy.viewer, targetCluster.centroid, undefined, {
        duration: 1.5,
      });
    }

    setScrollToClusterId(targetCluster.id);
    PendingResourceManager.remove(ResourceDeepLinkParam.HeapCluster);
  }, [isHeapListSuccess, isHeapListLoading, heapListResponse]);

  return (
    <div className="map-3d-heap-management__container">
      {selectedTerrainIteration?.id ? (
        <>
          <TabSwitcher
            tabComponentList={HeapClusterManagementTabs({
              heapListResponse,
              isHeapListLoading,
              heapListRefetch,
              selectedClusters,
              onSelectClusterHandler,
              setActiveTabKey,
              setIsHeapsTableVisible,
              isHeapsTableVisible,
              setIsHeapClusterModified,
              setActiveHeapCluster,
              activeHeapCluster,
              importWKTPolygon,
              scrollToClusterId,
              setScrollToClusterId,
            })}
            activeKey={activeTabKey}
            onTabSwitch={(tabKey) => onTabSwitch(tabKey)}
          />
          {isHeapsTableVisible && (
            <HeapClusterTable
              setIsHeapsTableVisible={setIsHeapsTableVisible}
              heapClustersOptions={heapClustersOptions || []}
              setActiveHeapCluster={setActiveHeapCluster}
              activeHeapCluster={activeHeapCluster}
              importWKTPolygon={importWKTPolygon}
              selectedClusters={selectedClusters}
              refetchHeapClusterList={heapListRefetch}
              isHeapClusterModified={isHeapClusterModified}
              setIsHeapClusterModified={setIsHeapClusterModified}
              selectedInactiveHeaps={selectedInactiveHeaps}
            />
          )}
        </>
      ) : (
        <Map3DNoTerrainSelected className="map-3d-sidebar-content" />
      )}
    </div>
  );
};
